/**
 * Layer the designed after photo over the full original so both sets of
 * teeth are visible at once. The smile box is kept for lip zoom.
 */

import {
  INNER_LOWER_LIP_CENTER,
  INNER_MOUTH_LEFT,
  INNER_MOUTH_RIGHT,
  INNER_UPPER_LIP_CENTER,
  MOUTH_COMMISSURE_LEFT,
  MOUTH_COMMISSURE_RIGHT,
  NOSE_TIP_INDEX,
  PHILTRUM_INDEX,
} from "./constants";
import { detectFaceLandmarks } from "./faceDetection";
import { toPixelCoords } from "./landmarkProcessing";
import {
  boundingBoxFromPoints,
  clampBoundingBox,
  extractSmileRegion,
} from "./smileRegionExtraction";
import type { BoundingBox, Point2D, SmileRegion } from "./types";

const ALIGN_INDICES = [
  MOUTH_COMMISSURE_LEFT,
  MOUTH_COMMISSURE_RIGHT,
  INNER_MOUTH_LEFT,
  INNER_MOUTH_RIGHT,
  INNER_UPPER_LIP_CENTER,
  INNER_LOWER_LIP_CENTER,
  NOSE_TIP_INDEX,
  PHILTRUM_INDEX,
] as const;

export interface SimilarityTransform {
  a: number;
  b: number;
  tx: number;
  ty: number;
}

export interface SuperimposePrepareResult {
  width: number;
  height: number;
  smileBox: BoundingBox;
  beforeUrl: string;
  alignedAfterUrl: string;
  aligned: boolean;
}

export interface SuperimposeBakeOptions {
  baseOpacity?: number;
  overlayOpacity?: number;
  overlayScale?: number;
  cropToSmile?: boolean;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image for superimpose"));
    img.src = src;
  });
}

function canvasToPng(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL("image/png");
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality = 0.92): string {
  return canvas.toDataURL("image/jpeg", quality);
}

function isNearIdentity(t: SimilarityTransform): boolean {
  return (
    Math.abs(t.a - 1) < 0.012 &&
    Math.abs(t.b) < 0.012 &&
    Math.abs(t.tx) < 2.5 &&
    Math.abs(t.ty) < 2.5
  );
}

function identityTransform(): SimilarityTransform {
  return { a: 1, b: 0, tx: 0, ty: 0 };
}

export function similarityFromPoints(
  src: Point2D[],
  dst: Point2D[]
): SimilarityTransform {
  const n = Math.min(src.length, dst.length);
  if (n < 2) return identityTransform();

  let sx = 0;
  let sy = 0;
  let dx = 0;
  let dy = 0;
  for (let i = 0; i < n; i++) {
    sx += src[i].x;
    sy += src[i].y;
    dx += dst[i].x;
    dy += dst[i].y;
  }
  sx /= n;
  sy /= n;
  dx /= n;
  dy /= n;

  let varSrc = 0;
  let a = 0;
  let b = 0;
  for (let i = 0; i < n; i++) {
    const xs = src[i].x - sx;
    const ys = src[i].y - sy;
    const xd = dst[i].x - dx;
    const yd = dst[i].y - dy;
    varSrc += xs * xs + ys * ys;
    a += xs * xd + ys * yd;
    b += xs * yd - ys * xd;
  }
  if (varSrc < 1e-6) {
    return { a: 1, b: 0, tx: dx - sx, ty: dy - sy };
  }
  a /= varSrc;
  b /= varSrc;
  return {
    a,
    b,
    tx: dx - (a * sx - b * sy),
    ty: dy - (b * sx + a * sy),
  };
}

function pickAlignPoints(pixel: Point2D[]): Point2D[] {
  const pts: Point2D[] = [];
  for (const i of ALIGN_INDICES) {
    if (pixel[i]) pts.push(pixel[i]);
  }
  return pts;
}

async function facePixels(
  img: HTMLImageElement
): Promise<{ pixel: Point2D[]; smile: SmileRegion } | null> {
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  const landmarks = await detectFaceLandmarks(img);
  if (!landmarks) return null;
  const pixel = toPixelCoords(landmarks, width, height);
  const smile = extractSmileRegion(pixel, width, height);
  if (!smile) return null;
  return { pixel, smile };
}

function warpAfterOntoBefore(
  before: HTMLImageElement,
  after: HTMLImageElement,
  transform: SimilarityTransform
): HTMLCanvasElement {
  const w = before.naturalWidth || before.width;
  const h = before.naturalHeight || before.height;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not create alignment canvas");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.setTransform(
    transform.a,
    transform.b,
    -transform.b,
    transform.a,
    transform.tx,
    transform.ty
  );
  ctx.drawImage(after, 0, 0);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  return canvas;
}

function smileFocusBox(
  beforeW: number,
  beforeH: number,
  beforeSmile: SmileRegion
): BoundingBox {
  const pts: Point2D[] = [
    ...beforeSmile.outerPolygon,
    ...beforeSmile.innerPolygon,
    ...beforeSmile.mouthLandmarks,
  ];
  const raw = boundingBoxFromPoints(pts, { x: 0.22, y: 0.42 });
  return clampBoundingBox(raw, beforeW, beforeH);
}

function containRect(
  imgW: number,
  imgH: number,
  canvasW: number,
  canvasH: number
) {
  const factor = Math.min(canvasW / imgW, canvasH / imgH);
  const w = imgW * factor;
  const h = imgH * factor;
  return { x: (canvasW - w) / 2, y: (canvasH - h) / 2, w, h };
}

function cropCanvas(
  source: HTMLCanvasElement,
  box: BoundingBox
): HTMLCanvasElement {
  const out = document.createElement("canvas");
  const x = Math.max(0, Math.round(box.x));
  const y = Math.max(0, Math.round(box.y));
  const w = Math.max(1, Math.round(box.width));
  const h = Math.max(1, Math.round(box.height));
  out.width = w;
  out.height = h;
  const ctx = out.getContext("2d");
  if (!ctx) throw new Error("Could not crop superimposed image");
  ctx.drawImage(source, x, y, w, h, 0, 0, w, h);
  return out;
}

/**
 * Align the designed after onto the original and locate the smile crop.
 */
export async function prepareSmileSuperimpose(
  beforeSrc: string,
  afterSrc: string
): Promise<SuperimposePrepareResult> {
  const [beforeImg, afterImg] = await Promise.all([
    loadImage(beforeSrc),
    loadImage(afterSrc),
  ]);
  const beforeW = beforeImg.naturalWidth || beforeImg.width;
  const beforeH = beforeImg.naturalHeight || beforeImg.height;
  const afterW = afterImg.naturalWidth || afterImg.width;
  const afterH = afterImg.naturalHeight || afterImg.height;
  if (!beforeW || !beforeH || !afterW || !afterH) {
    throw new Error("Invalid image dimensions for superimpose");
  }

  const [beforeFace, afterFace] = await Promise.all([
    facePixels(beforeImg),
    facePixels(afterImg),
  ]);

  let transform = identityTransform();
  let aligned = false;
  if (beforeFace && afterFace) {
    const src = pickAlignPoints(afterFace.pixel);
    const dst = pickAlignPoints(beforeFace.pixel);
    if (src.length >= 2 && src.length === dst.length) {
      transform = similarityFromPoints(src, dst);
      aligned = true;
    }
  } else if (afterW !== beforeW || afterH !== beforeH) {
    const r = containRect(afterW, afterH, beforeW, beforeH);
    transform = {
      a: r.w / afterW,
      b: 0,
      tx: r.x,
      ty: r.y,
    };
  }

  const identity = isNearIdentity(transform);
  let beforeUrl = beforeSrc;
  let alignedAfterUrl = afterSrc;

  if (!identity) {
    const beforeCanvas = document.createElement("canvas");
    beforeCanvas.width = beforeW;
    beforeCanvas.height = beforeH;
    const bctx = beforeCanvas.getContext("2d");
    if (!bctx) throw new Error("Could not create before canvas");
    bctx.drawImage(beforeImg, 0, 0, beforeW, beforeH);
    beforeUrl = canvasToJpeg(beforeCanvas);
    alignedAfterUrl = canvasToJpeg(
      warpAfterOntoBefore(beforeImg, afterImg, transform)
    );
  }

  const smileBox = beforeFace
    ? smileFocusBox(beforeW, beforeH, beforeFace.smile)
    : {
        x: Math.round(beforeW * 0.08),
        y: Math.round(beforeH * 0.28),
        width: Math.round(beforeW * 0.84),
        height: Math.round(beforeH * 0.42),
      };

  return {
    width: beforeW,
    height: beforeH,
    smileBox,
    beforeUrl,
    alignedAfterUrl,
    aligned: aligned || identity,
  };
}

/**
 * Flatten the live overlay into a full-frame PNG.
 * Pass cropToSmile to export only the smile region.
 */
export async function bakeSmileSuperimpose(
  prepared: SuperimposePrepareResult,
  options: SuperimposeBakeOptions = {}
): Promise<string> {
  const {
    baseOpacity = 1,
    overlayOpacity = 0.5,
    overlayScale = 1,
    cropToSmile = false,
  } = options;

  const [beforeImg, overlayImg] = await Promise.all([
    loadImage(prepared.beforeUrl),
    loadImage(prepared.alignedAfterUrl),
  ]);

  const canvas = document.createElement("canvas");
  canvas.width = prepared.width;
  canvas.height = prepared.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not create superimposed image");

  ctx.globalAlpha = Math.max(0, Math.min(1, baseOpacity));
  ctx.drawImage(beforeImg, 0, 0, prepared.width, prepared.height);

  const cx = prepared.smileBox.x + prepared.smileBox.width / 2;
  const cy = prepared.smileBox.y + prepared.smileBox.height / 2;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(overlayScale, overlayScale);
  ctx.translate(-cx, -cy);
  ctx.globalAlpha = Math.max(0, Math.min(1, overlayOpacity));
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(overlayImg, 0, 0, prepared.width, prepared.height);
  ctx.restore();
  ctx.globalAlpha = 1;

  const out = cropToSmile ? cropCanvas(canvas, prepared.smileBox) : canvas;
  return canvasToPng(out);
}
